import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { offersAPI } from "../../api/client.js";
import {
  getOfferCompensationSummary,
  formatInr,
} from "../../utils/offerSalary";
import "./OfferReleaseChecklistModal.css";

export default function OfferReleaseChecklistModal({
  offer,
  open,
  onClose,
  onGenerate,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // The full offer row, fetched on open so that the compensation shown on the
  // release page is always the stored one and never a stale list snapshot.
  const [offerDetails, setOfferDetails] = useState(null);

  const offerId = offer?.offer_id || offer?.id;

  const compensation = useMemo(
    () => getOfferCompensationSummary(offerDetails || offer || {}),
    [offerDetails, offer]
  );

  const answeredYes = useMemo(
    () => items.filter((item) => item.answer === "Yes").length,
    [items]
  );

  const answeredNo = useMemo(
    () => items.filter((item) => item.answer === "No").length,
    [items]
  );

  const unanswered = useMemo(
    () => items.filter((item) => !item.answer).length,
    [items]
  );

  const allAnswered =
    items.length > 0 &&
    unanswered === 0;

  const allYes =
    items.length > 0 &&
    allAnswered &&
    answeredNo === 0 &&
    answeredYes === items.length;

  const loadChecklist = async () => {
    if (!offerId) return;

    try {
      setLoading(true);

      // IMPORTANT:
      // client.js exposes getOfferReleaseChecklist(), not
      // getOfferReleaseChecklist().
      const response =
        await offersAPI.getOfferReleaseChecklist(offerId);

      const checklist =
        response.data?.checklist || {};

      setItems(
        Array.isArray(checklist.items)
          ? checklist.items
          : []
      );
    } catch (error) {
      console.error(
        "Failed to load release checklist:",
        error
      );

      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to load release checklist"
      );

      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  // The compensation block is fetched separately so that a failure here never
  // blocks the checklist itself.
  const loadOfferDetails = async () => {
    if (!offerId) return;

    try {
      const response = await offersAPI.getById(offerId);

      if (response?.data) {
        setOfferDetails(response.data);
      }
    } catch (error) {
      console.error(
        "Failed to load offer compensation:",
        error
      );
    }
  };

  useEffect(() => {
    if (open && offerId) {
      loadChecklist();
      loadOfferDetails();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offerId]);

  if (!open) {
    return null;
  }

  const updateItem = (id, field, value) => {
    setItems((previous) =>
      previous.map((item) =>
        String(item.id) === String(id)
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const buildChecklistPayload = () => ({
    items: items.map((item) => ({
      questionId: item.id,
      answer: item.answer,
      comments: item.comments || "",
    })),
  });

  const handleSaveChecklist = async () => {
    if (!offerId) {
      toast.error("Offer ID is missing");
      return null;
    }

    if (items.some((item) => !item.answer)) {
      toast.error(
        "Please answer every checklist question before saving."
      );
      return null;
    }

    try {
      setSaving(true);

      // IMPORTANT:
      // client.js exposes saveOfferReleaseChecklist(), not
      // saveOfferReleaseChecklist().
      const response =
        await offersAPI.saveOfferReleaseChecklist(
          offerId,
          buildChecklistPayload()
        );

      const checklist =
        response.data?.checklist;

      setItems(
        Array.isArray(checklist?.items)
          ? checklist.items
          : items
      );

      toast.success(
        checklist?.complete
          ? "Checklist passed. Offer letter can be generated."
          : "Checklist saved successfully."
      );

      return checklist || null;
    } catch (error) {
      console.error(
        "Checklist save error:",
        error
      );

      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to save checklist"
      );

      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!offerId) {
      toast.error("Offer ID is missing");
      return;
    }

    if (!allAnswered) {
      toast.error(
        "Please answer every checklist question with Yes or No."
      );
      return;
    }

    if (!allYes) {
      toast.error(
        "Every checklist answer must be Yes before the offer can be generated."
      );
      return;
    }

    try {
      setSaving(true);

      const checklist =
        await handleSaveChecklist();

      if (!checklist?.complete) {
        toast.error(
          "Checklist is not complete. All answers must be Yes."
        );
        return;
      }

      // Do NOT release here.
      // Generate first. The next modal shows the generated
      // offer letter and provides the final Release button.
      await onGenerate?.(offer);
    } catch (error) {
      console.error(
        "Generate offer error:",
        error
      );

      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate offer letter"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="release-checklist-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="release-checklist-title"
    >
      <div className="release-checklist-modal">
        <div className="release-checklist-header">
          <div>
            <div className="release-checklist-eyebrow">
              HR RELEASE CONTROL
            </div>

            <h2 id="release-checklist-title">
              Offer Release Checklist
            </h2>

            <p>
              Verify every item before generating the final
              offer letter.
            </p>
          </div>

          <button
            type="button"
            className="release-checklist-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="release-checklist-offer-summary">
          <div>
            <span>Candidate</span>
            <strong>
              {offer?.candidate_name || "-"}
            </strong>
          </div>

          <div>
            <span>Position</span>
            <strong>
              {offer?.position_offered ||
                offer?.position ||
                "-"}
            </strong>
          </div>

          <div>
            <span>Offer ID</span>
            <strong>
              #{offerId || "-"}
            </strong>
          </div>

          {compensation.hasCompensation && (
            <div>
              <span>Total CTC (E+F)</span>
              <strong>
                {formatInr(compensation.total)}
              </strong>
            </div>
          )}
        </div>

        {/* Everything between the pinned offer summary and the pinned
            footer lives in one scroll region. The checklist table used to
            be the scroll container on its own, and it collapsed to zero
            height whenever the sections above it grew taller than the
            modal - which made the checklist invisible and impossible to
            scroll. */}
        <div className="release-checklist-scroll">
          {/* Compensation breakdown - section E and F of Annexure-A. The
              variable lines only appear when a variable pay was explicitly
              entered for this offer, so a release never hides an incentive
              behind a single total. */}
          {compensation.hasCompensation && (
            <div className="release-checklist-compensation">
              <div className="release-checklist-compensation-title">
                Compensation
              </div>

              <div className="release-checklist-compensation-row">
                <span>Fixed CTC</span>
                <strong>{formatInr(compensation.fixed)}</strong>
              </div>

              <div className="release-checklist-compensation-row">
                <span>Total Fixed (E)</span>
                <strong>{formatInr(compensation.fixedTotal)}</strong>
              </div>

              {compensation.hasVariable && (
                <>
                  <div className="release-checklist-compensation-row">
                    <span>
                      Variable - {compensation.variableType || "Billable Bonus"}
                    </span>
                    <strong>
                      {formatInr(compensation.variableAmount)}
                      {compensation.variableAmount > 0
                        ? ` per ${compensation.variablePeriodLabel.toLowerCase()}`
                        : ""}
                    </strong>
                  </div>

                  <div className="release-checklist-compensation-row">
                    <span>Variable (Annual)</span>
                    <strong>
                      {formatInr(compensation.variableAmountAnnual)}
                    </strong>
                  </div>

                  {compensation.annualVariablePay > 0 && (
                    <div className="release-checklist-compensation-row">
                      <span>Annual Variable Pay</span>
                      <strong>
                        {formatInr(compensation.annualVariablePay)}
                      </strong>
                    </div>
                  )}

                  {compensation.retentionBonus > 0 && (
                    <div className="release-checklist-compensation-row">
                      <span>Annual Retention Bonus</span>
                      <strong>
                        {formatInr(compensation.retentionBonus)}
                      </strong>
                    </div>
                  )}

                  <div className="release-checklist-compensation-row">
                    <span>Total Variable (F)</span>
                    <strong>
                      {formatInr(compensation.variableTotal)}
                    </strong>
                  </div>
                </>
              )}

              <div className="release-checklist-compensation-row total">
                <span>Total CTC (E+F)</span>
                <strong>{formatInr(compensation.total)}</strong>
              </div>
            </div>
          )}

          <div className="release-checklist-progress">
            <div>
              <span>Checklist progress</span>
              <strong>
                {answeredYes} / {items.length} Yes
              </strong>
            </div>

            <div className="release-checklist-progress-bar">
              <span
                style={{
                  width:
                    items.length > 0
                      ? `${(answeredYes / items.length) * 100}%`
                      : "0%",
                }}
              />
            </div>

            <div className="release-checklist-stats">
              <span className="check-stat yes">
                Yes: {answeredYes}
              </span>

              <span className="check-stat no">
                No: {answeredNo}
              </span>

              <span className="check-stat pending">
                Pending: {unanswered}
              </span>
            </div>
          </div>

        <div className="release-checklist-body">
          {loading ? (
            <div className="release-checklist-loading">
              Loading checklist...
            </div>
          ) : items.length === 0 ? (
            <div className="release-checklist-empty">
              No active checklist questions were found.
            </div>
          ) : (
            <div className="release-checklist-table-wrapper">
              <table className="release-checklist-table">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Verification item</th>
                    <th scope="col">Answer</th>
                    <th scope="col">Comments</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.id}>
                      <td className="check-number">
                        {item.display_order || index + 1}
                      </td>

                      <td className="check-question">
                        {item.question}
                      </td>

                      <td>
                        <div className="check-answer-buttons">
                          <button
                            type="button"
                            className={
                              item.answer === "Yes"
                                ? "check-answer yes selected"
                                : "check-answer yes"
                            }
                            onClick={() =>
                              updateItem(
                                item.id,
                                "answer",
                                "Yes"
                              )
                            }
                            disabled={saving}
                          >
                            ✓ Yes
                          </button>

                          <button
                            type="button"
                            className={
                              item.answer === "No"
                                ? "check-answer no selected"
                                : "check-answer no"
                            }
                            onClick={() =>
                              updateItem(
                                item.id,
                                "answer",
                                "No"
                              )
                            }
                            disabled={saving}
                          >
                            ✕ No
                          </button>
                        </div>
                      </td>

                      <td>
                        <textarea
                          value={
                            item.comments || ""
                          }
                          onChange={(event) =>
                            updateItem(
                              item.id,
                              "comments",
                              event.target.value
                            )
                          }
                          placeholder="Optional item comment"
                          rows={2}
                          maxLength={1000}
                          disabled={saving}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>

        <div className="release-checklist-footer">
          <div className="release-checklist-footer-message">
            {allYes ? (
              <span className="ready-message">
                ✓ All checks passed. Generate the offer letter.
              </span>
            ) : answeredNo > 0 ? (
              <span>
                ✕ One or more checks are No. The offer cannot be generated.
              </span>
            ) : (
              <span>
                Answer every checklist question. Generate is enabled only
                when all answers are Yes.
              </span>
            )}
          </div>

          <div className="release-checklist-actions">
            <button
              type="button"
              className="release-checklist-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="button"
              className="release-checklist-save"
              onClick={handleSaveChecklist}
              disabled={
                saving ||
                loading ||
                !items.length ||
                !allAnswered
              }
            >
              {saving
                ? "Saving..."
                : "Save Checklist"}
            </button>

            {allYes && (
              <button
                type="button"
                className="release-checklist-release"
                onClick={handleGenerate}
                disabled={
                  saving ||
                  loading ||
                  !items.length ||
                  !allYes
                }
              >
                {saving
                  ? "Generating..."
                  : "Generate Offer Letter"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}